import { getChallengeDays } from '@/constants/challenge';
import i18n from '@/i18n';
import { getTodayCH } from '@/utils/date';
import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

/**
 * Libellé localisé d'un jour du challenge (ex. FR « Jeu. 17 septembre »,
 * EN "Thu 17 September"). Équivalent i18n de formatChallengeDayFR
 * (constants/challenge.ts, non modifiable) : noms de jour/mois et format via
 * les clés `dates.*`. Jour de semaine calculé en UTC — aucun fuseau appareil.
 * Utilisé aussi par app/(tabs)/quiz.tsx.
 */
export function formatChallengeDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const weekdayIndex = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return i18n.t('dates.challengeDayFormat', {
    weekday: i18n.t(`dates.weekdayShort${weekdayIndex}`),
    day: d,
    month: i18n.t(`dates.month${m}`),
  });
}

interface DateSelectorProps {
  /** Jour sélectionné, 'YYYY-MM-DD' (jour suisse). */
  selectedDate: string;
  onDateChange: (date: string) => void;
}

export default function DateSelector({ selectedDate, onDateChange }: DateSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Dates forcées sur la fenêtre du challenge (14–25 septembre), pas la
  // semaine en cours. Chaînes 'YYYY-MM-DD' de bout en bout : l'affichage,
  // la comparaison et la requête DB parlent du même jour quel que soit le
  // fuseau de l'appareil.
  const weekDates = getChallengeDays();
  const todayCH = getTodayCH();

  return (
    <>
      <TouchableOpacity
        style={styles.selector}
        onPress={() => setIsOpen(true)}
        activeOpacity={0.7}
      >
        <Text style={styles.selectorText}>{formatChallengeDay(selectedDate)}</Text>
        <Ionicons name="chevron-down" size={20} color="#000" />
      </TouchableOpacity>

      <Modal
        animationType="fade"
        transparent={true}
        visible={isOpen}
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View style={styles.modalContent}>
            {weekDates.map((date) => {
              const isSelected = date === selectedDate;
              const isToday = date === todayCH;

              return (
                <TouchableOpacity
                  key={date}
                  style={[
                    styles.dateOption,
                    isSelected && styles.dateOptionSelected
                  ]}
                  onPress={() => {
                    onDateChange(date);
                    setIsOpen(false);
                  }}
                >
                  <Text style={[
                    styles.dateOptionText,
                    isSelected && styles.dateOptionTextSelected
                  ]}>
                    {formatChallengeDay(date)}{isToday ? `  ${t('dates.todaySuffix')}` : ''}
                  </Text>
                  {isSelected && (
                    <Ionicons name="checkmark" size={24} color="#000" />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  selectorText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'flex-start',
    paddingTop: 120,
    paddingHorizontal: 24,
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  dateOption: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFF',
  },
  dateOptionSelected: {
    backgroundColor: '#E8F5E9',
  },
  dateOptionText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 16,
    color: '#000',
  },
  dateOptionTextSelected: {
    fontFamily: 'Inter_700Bold',
  },
});
